import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMap } from 'react-leaflet'
import L from 'leaflet'

import {
  beginEntityDrag,
  dispatchEntityDropAtPoint,
  endEntityDrag,
  getEntityDrag,
  getEntityDragPointer,
  moveEntityDrag,
  subscribeEntityDrag,
  type EntityDragPayload,
} from '../entityDrag'

import { stepPointRef, type OperationMapPoint } from '../operations'

export const ENTITY_DRAG_THRESHOLD_PX = 6

/**
 * Pointer-drag a map marker as a ghost clone (original stays put).
 * Drop onto EntitySearch fields that opt into acceptEntityDrop.
 */
export function useMapEntityDrag(args: {
  markerRef: React.RefObject<L.Marker | null>
  payload: EntityDragPayload | null
  /** Bump when the marker icon DOM may have been replaced (selection/focus). */
  remountKey?: string | number | boolean
}): React.MutableRefObject<boolean> {
  const map = useMap()
  const suppressClickRef = useRef(false)
  const payloadRef = useRef(args.payload)
  payloadRef.current = args.payload

  useEffect(() => {
    const marker = args.markerRef.current
    if (!marker) return

    let el: HTMLElement | null = null
    let pointerId: number | null = null
    let startX = 0
    let startY = 0
    let dragging = false
    let mapDragWasEnabled = false

    const onPointerMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      const payload = payloadRef.current
      if (!payload) return
      const dx = event.clientX - startX
      const dy = event.clientY - startY
      if (!dragging) {
        if (Math.hypot(dx, dy) < ENTITY_DRAG_THRESHOLD_PX) return
        dragging = true
        suppressClickRef.current = true
        mapDragWasEnabled = map.dragging.enabled()
        if (mapDragWasEnabled) map.dragging.disable()
        document.body.style.userSelect = 'none'
        beginEntityDrag(payload, event.clientX, event.clientY)
        try {
          el?.setPointerCapture(event.pointerId)
        } catch {
          /* ignore */
        }
      }
      moveEntityDrag(event.clientX, event.clientY)
      event.preventDefault()
      event.stopPropagation()
    }

    const finish = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      pointerId = null
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', finish, true)
      window.removeEventListener('pointercancel', finish, true)
      if (!dragging) return
      dragging = false
      document.body.style.userSelect = ''
      if (mapDragWasEnabled) map.dragging.enable()
      const payload = endEntityDrag()
      if (payload) {
        dispatchEntityDropAtPoint(event.clientX, event.clientY, payload)
      }
      event.preventDefault()
      event.stopPropagation()
      window.setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return
      if (!payloadRef.current) return
      pointerId = event.pointerId
      startX = event.clientX
      startY = event.clientY
      dragging = false
      window.addEventListener('pointermove', onPointerMove, true)
      window.addEventListener('pointerup', finish, true)
      window.addEventListener('pointercancel', finish, true)
    }

    const detach = () => {
      if (!el) return
      el.removeEventListener('pointerdown', onPointerDown)
      el.style.cursor = ''
      el = null
    }

    const attach = () => {
      detach()
      el = marker.getElement() ?? null
      if (!el) return
      el.style.cursor = 'grab'
      el.addEventListener('pointerdown', onPointerDown)
    }

    marker.on('add', attach)
    // Icon updates can replace the element without a fresh 'add'.
    window.setTimeout(attach, 0)

    return () => {
      marker.off('add', attach)
      detach()
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', finish, true)
      window.removeEventListener('pointercancel', finish, true)
      if (dragging) {
        document.body.style.userSelect = ''
        if (mapDragWasEnabled) map.dragging.enable()
        endEntityDrag()
      }
    }
  }, [args.markerRef, args.remountKey, map])

  return suppressClickRef
}

/**
 * Pointer-drag a DOM element (e.g. pinned omni badge) into EntitySearch drop fields.
 */
export function useDomEntityDrag(
  payload: EntityDragPayload | null,
  mapRef?: React.RefObject<L.Map | null>,
): {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  suppressClickRef: React.MutableRefObject<boolean>
} {
  const suppressClickRef = useRef(false)
  const payloadRef = useRef(payload)
  payloadRef.current = payload
  const mapRefStable = useRef(mapRef)
  mapRefStable.current = mapRef

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    if (!payloadRef.current) return
    // Don't preventDefault — that would suppress the button's click (focus).
    // Capture keeps move/up events on this element so the map can't steal the gesture.
    event.stopPropagation()

    const el = event.currentTarget
    const pointerId = event.pointerId
    const startX = event.clientX
    const startY = event.clientY
    let dragging = false
    let mapDragWasEnabled = false
    let finished = false

    try {
      el.setPointerCapture(pointerId)
    } catch {
      /* ignore */
    }

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== pointerId) return
      const nextPayload = payloadRef.current
      if (!nextPayload) return
      const dx = moveEvent.clientX - startX
      const dy = moveEvent.clientY - startY
      if (!dragging) {
        if (Math.hypot(dx, dy) < ENTITY_DRAG_THRESHOLD_PX) return
        dragging = true
        suppressClickRef.current = true
        const map = mapRefStable.current?.current
        if (map) {
          mapDragWasEnabled = map.dragging.enabled()
          if (mapDragWasEnabled) map.dragging.disable()
        }
        document.body.style.userSelect = 'none'
        beginEntityDrag(nextPayload, moveEvent.clientX, moveEvent.clientY)
      }
      moveEntityDrag(moveEvent.clientX, moveEvent.clientY)
      moveEvent.preventDefault()
      moveEvent.stopPropagation()
    }

    const finish = (upEvent: PointerEvent) => {
      if (upEvent.pointerId !== pointerId) return
      if (finished) return
      finished = true
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', finish)
      el.removeEventListener('pointercancel', finish)
      window.removeEventListener('pointermove', onPointerMove, true)
      window.removeEventListener('pointerup', finish, true)
      window.removeEventListener('pointercancel', finish, true)
      try {
        if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId)
      } catch {
        /* ignore */
      }
      if (!dragging) return
      dragging = false
      document.body.style.userSelect = ''
      if (mapDragWasEnabled) mapRefStable.current?.current?.dragging.enable()
      const dropped = endEntityDrag()
      if (dropped) {
        dispatchEntityDropAtPoint(upEvent.clientX, upEvent.clientY, dropped)
      }
      upEvent.preventDefault()
      upEvent.stopPropagation()
      window.setTimeout(() => {
        suppressClickRef.current = false
      }, 0)
    }

    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', finish)
    el.addEventListener('pointercancel', finish)
    // Fallback if capture is unavailable or lost mid-gesture.
    window.addEventListener('pointermove', onPointerMove, true)
    window.addEventListener('pointerup', finish, true)
    window.addEventListener('pointercancel', finish, true)
  }, [])

  return { onPointerDown, suppressClickRef }
}

export function EntityDragGhost() {
  const [tick, setTick] = useState(0)
  useEffect(() => subscribeEntityDrag(() => setTick((n) => n + 1)), [])
  void tick
  const active = getEntityDrag()
  if (!active) return null
  const { x, y } = getEntityDragPointer()
  return createPortal(
    <div
      className="pointer-events-none fixed z-[100000] flex max-w-[14rem] items-center gap-2 rounded-lg border-2 border-base-300 bg-base-100 px-2.5 py-1.5 shadow-xl"
      style={{ left: x, top: y, transform: 'translate(-50%, -120%)' }}
      aria-hidden
    >
      <span
        className="h-3 w-3 shrink-0 rounded-sm"
        style={{ backgroundColor: active.color }}
      />
      <span className="truncate text-xs font-semibold text-base-content">{active.label}</span>
    </div>,
    document.body,
  )
}

export function dragPayloadForResultPoint(
  point: OperationMapPoint,
  fromStepId: string,
): EntityDragPayload {
  // Standalone place pins (e.g. "My location") are not owned by a step — encode as
  // lat,lng so destination resolution can treat them like an address coordinate pin.
  if (!fromStepId) {
    return {
      refId: `${point.lat},${point.lng}`,
      label: point.name,
      color: point.color,
    }
  }
  // Always encode as a step-point ref so destination fields / ETA chaining treat this
  // as "use this operation result" (append a step) rather than a bare catalog entity
  // (which would overwrite the active step).
  return {
    refId: stepPointRef(fromStepId, point.id),
    label: point.name,
    color: point.color,
  }
}

